/**
 * 源石币日志 — GET /api/admin/uoc/logs
 *
 * Read-only paginated view of UOC-related ledger entries: mint, burn, admin recharge.
 * Supports action + user_id filters.
 */
import { useState } from 'react';
import { Input, Select, Space, Tag } from 'antd';
import { DataTable } from '../../components/DataTable';

interface LogRow {
  id: string;
  user_id: number;
  asset: string;
  action: string;
  delta: string;
  balance_before: string;
  balance_after: string;
  ref: string | null;
  note: string | null;
  create_time: string;
}

const ACTION_OPTIONS = [
  { label: '全部', value: '' },
  { label: 'UOC 铸造', value: 'uoc_mint' },
  { label: 'UOC 销毁', value: 'uoc_burn' },
  { label: '管理员充值', value: 'uoc_admin_recharge' },
];

const ACTION_COLOR: Record<string, string> = {
  uoc_mint: 'green',
  uoc_burn: 'red',
  uoc_admin_recharge: 'blue',
};

const ACTION_LABEL: Record<string, string> = {
  uoc_mint: '铸造',
  uoc_burn: '销毁',
  uoc_admin_recharge: '充值',
};

export function UocLogPage() {
  const [actionFilter, setActionFilter] = useState<string | undefined>(undefined);
  const [userIdFilter, setUserIdFilter] = useState<string | undefined>(undefined);

  const extra: Record<string, string> = {};
  if (actionFilter) extra.action = actionFilter;
  if (userIdFilter) extra.user_id = userIdFilter;

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '用户 ID', dataIndex: 'user_id', width: 90 },
    {
      title: '资产',
      dataIndex: 'asset',
      width: 80,
      render: (v: string) => v.toUpperCase(),
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (v: string) => (
        <Tag color={ACTION_COLOR[v] ?? 'default'}>{ACTION_LABEL[v] ?? v}</Tag>
      ),
    },
    {
      title: '变动',
      dataIndex: 'delta',
      width: 140,
      render: (v: string) => {
        const n = Number(v);
        return (
          <span style={{ color: n >= 0 ? '#52c41a' : '#ff4d4f' }}>
            {n >= 0 ? '+' : ''}{n.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: '变动前余额',
      dataIndex: 'balance_before',
      width: 140,
      render: (v: string) => Number(v).toLocaleString(),
    },
    {
      title: '变动后余额',
      dataIndex: 'balance_after',
      width: 140,
      render: (v: string) => Number(v).toLocaleString(),
    },
    { title: 'Ref', dataIndex: 'ref', ellipsis: true, width: 200 },
    { title: '备注', dataIndex: 'note', ellipsis: true, width: 200 },
    {
      title: '时间',
      dataIndex: 'create_time',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space>
        <Select
          style={{ width: 160 }}
          placeholder="操作类型"
          allowClear
          options={ACTION_OPTIONS.filter((o) => o.value)}
          onChange={(v) => setActionFilter(v || undefined)}
        />
        <Input
          style={{ width: 120 }}
          placeholder="用户 ID"
          allowClear
          onChange={(e) => setUserIdFilter(e.target.value || undefined)}
        />
      </Space>
      <DataTable<LogRow>
        queryKey={['admin', 'uoc-logs', actionFilter, userIdFilter]}
        endpoint="/api/admin/uoc/logs"
        extraParams={extra}
        rowKey="id"
        columns={columns}
      />
    </div>
  );
}
